import React from 'react';
import useExcelStyles from '../useExcelStyles';
import useNoRowsTemplate from '../useNoRowsOverlay';
import useLoadingOverlay from '../useLoadingOverlay';
import { useTranslation } from 'react-i18next';
import { useDispatch } from '../../../redux/store';
import { setDialogMode, openDialog, selectInformation } from '../../../redux/slices/information';
import dateTimeCellCallback from '../../../utils/ag-grid/processCellCallback/dateTimeCellCallback';
import emailCellCallback from '../../../utils/ag-grid/processCellCallback/emailCellCallback';
import { TOOLTIP_SHOW_DELAY } from '../../../constants';
import type { InformationType } from '../../../types/information';
import type { ProcessCellCallbackParams } from '../../../types/common';
import type { ColDef, ExcelStyle, GetContextMenuItemsParams, GridOptions, MenuItemDef } from 'ag-grid-community';

const useGridOptionsManageInformation = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { commonExcelStyles } = useExcelStyles();

  // Excelのスタイル設定
  // ----------------------------------------
  const excelStyles: ExcelStyle[] = [...commonExcelStyles];

  // Overlayの設定
  // ----------------------------------------
  const overlayLoadingTemplate: string = useLoadingOverlay();
  const overlayNoRowsTemplate: string = useNoRowsTemplate();

  // rowNodeIdの設定
  // ----------------------------------------
  const getRowNodeId = (params: InformationType): string => {
    if (params.id !== undefined) {
      return String(params.id);
    } else {
      return params.createdAt;
    }
  };

  // 右クリックメニューの設定
  // ----------------------------------------
  const getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    // console.log(params);
    if (params.node) {
      const data: InformationType = params.node.data;
      // Excel用の変換を行う
      const processCellCallback = (params: ProcessCellCallbackParams): string => {
        // console.log(params.value);
        const myColDef: ColDef = params.column.getColDef();
        const myField: string | undefined = myColDef.field ?? myColDef.colId;
        if (myField === 'createdAt' || myField === 'updatedAt') {
          const formattedValue: string = dateTimeCellCallback(params);
          // console.log(formattedValue);
          return formattedValue;
        } else if (myField === 'createdBy' || myField === 'updatedBy') {
          const formattedValue: string = emailCellCallback(params);
          // console.log(formattedValue);
          return formattedValue;
        } else {
          return params.value ?? '';
        }
      };
      // カスタムメニューの作成
      const editMenu = {
        name: t('edit'),
        action: () => {
          dispatch(selectInformation(data));
          dispatch(setDialogMode('UPDATE'));
          dispatch(openDialog());
        },
      };
      const deleteMenu = {
        name: t('delete'),
        action: () => {
          dispatch(selectInformation(data));
          dispatch(setDialogMode('DELETE'));
          dispatch(openDialog());
        },
      };
      const exportMenu = {
        icon: '<span class="ag-icon ag-icon-save" unselectable="on"></span>',
        name: 'Export',
        subMenu: [
          {
            name: 'CSV Export',
            action: () => params.api.exportDataAsCsv({ processCellCallback }),
          },
          {
            name: 'Excel Export (.xlsx)',
            action: () => params.api.exportDataAsExcel({ processCellCallback }),
          },
          // {
          //   name: 'Excel Export (.xml)',
          //   action: () => params.api.exportDataAsExcel({ exportMode: 'xml' }),
          // },
        ],
      };

      const menuItems = [editMenu, deleteMenu, 'separator', exportMenu];
      return menuItems;
    } else {
      return [];
    }
  };

  // GridOptions全体
  // ----------------------------------------
  const gridOptions: GridOptions = {
    allowContextMenuWithControlKey: true,
    enableRangeSelection: true,
    excelStyles,
    getContextMenuItems,
    getRowNodeId,
    overlayLoadingTemplate,
    overlayNoRowsTemplate,
    tooltipShowDelay: TOOLTIP_SHOW_DELAY,
  };

  return { gridOptions };
};

export default useGridOptionsManageInformation;
